import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { CheckCircle2, Download, Phone, Mail, ArrowRight } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useSiteConfig, useSupportPhone } from "@/context/SiteConfigContext";
import { toast } from "sonner";

/**
 * Post-payment thank-you page. Renders order confirmation, contact info,
 * and a receipt download button. Reads order metadata from the query
 * string (`invoice_id`, `invoice_number`, `order_number`, `total`,
 * `email`) so we never need an extra API call to render — the checkout
 * handler passes them straight through.
 */
export default function ThankYouPage() {
  const [params] = useSearchParams();
  const invoiceId = params.get("invoice_id") || "";
  const invoiceNumber = params.get("invoice_number") || "";
  const orderNumber = params.get("order_number") || "";
  const total = params.get("total") || "";
  const email = params.get("email") || "";
  const { config } = useSiteConfig();
  const { display: phoneDisplay, telHref } = useSupportPhone();
  const supportEmail = config.support_email || "info@stewardindia.com";
  const [downloading, setDownloading] = useState(false);
  const [urlValid] = useState(!!invoiceId);

  useEffect(() => {
    // A courtesy toast on mount so shoppers know the page finished
    // rendering. Non-blocking — the visible layout is the primary
    // confirmation cue.
    toast.success("Payment successful — thank you!");
  }, []);

  const downloadReceipt = async () => {
    if (!invoiceId) return;
    setDownloading(true);
    try {
      const resp = await api.get(`/learner/invoices/${invoiceId}/download`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([resp.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `${invoiceNumber || "Steward-Invoice"}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#101876]/5 to-white px-4 py-12" data-testid="thank-you-page">
      <div className="mx-auto max-w-2xl rounded-3xl border border-slate-200 bg-white p-8 sm:p-12 shadow-lg">
        <div className="flex items-center justify-center">
          <div className="flex h-20 w-20 items-center justify-center rounded-full bg-[#329112]/10">
            <CheckCircle2 className="h-12 w-12 text-[#329112]" />
          </div>
        </div>
        <h1 className="mt-6 text-center text-3xl font-black text-slate-900">Thank you for your payment!</h1>
        <p className="mt-2 text-center text-sm text-slate-600">
          Your purchase is confirmed. Your courses are unlocked in your dashboard and a copy of the invoice has been emailed to {email ? <b>{email}</b> : "you"}.
        </p>

        <div className="mt-8 grid gap-3 sm:grid-cols-2 text-sm">
          {orderNumber && (
            <div className="rounded-xl border border-slate-200 bg-slate-50/60 p-4">
              <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500">Order</div>
              <div className="mt-1 font-mono text-sm font-bold text-slate-900" data-testid="thanks-order-number">{orderNumber}</div>
            </div>
          )}
          {invoiceNumber && (
            <div className="rounded-xl border border-slate-200 bg-slate-50/60 p-4">
              <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500">Invoice</div>
              <div className="mt-1 font-mono text-sm font-bold text-slate-900" data-testid="thanks-invoice-number">{invoiceNumber}</div>
            </div>
          )}
          {total && (
            <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4 sm:col-span-2">
              <div className="text-[10px] font-bold uppercase tracking-widest text-emerald-700">Amount Paid</div>
              <div className="mt-1 text-2xl font-black text-emerald-900" data-testid="thanks-total">₹{total}</div>
            </div>
          )}
        </div>

        <div className="mt-8 flex flex-col gap-3 sm:flex-row">
          <button
            type="button"
            onClick={downloadReceipt}
            disabled={!urlValid || downloading}
            className="inline-flex flex-1 items-center justify-center gap-2 rounded-xl bg-[#101876] px-5 py-3 text-sm font-bold text-white hover:bg-[#0c135c] disabled:opacity-60"
            data-testid="thanks-download-receipt"
          >
            <Download className="h-4 w-4" /> {downloading ? "Preparing…" : "Download receipt (PDF)"}
          </button>
          <Link
            to="/app/learner/courses"
            className="inline-flex flex-1 items-center justify-center gap-2 rounded-xl border-2 border-[#101876] bg-white px-5 py-3 text-sm font-bold text-[#101876] hover:bg-[#101876]/5"
            data-testid="thanks-go-to-dashboard"
          >
            Go to my courses <ArrowRight className="h-4 w-4" />
          </Link>
        </div>

        <div className="mt-10 rounded-2xl border border-slate-200 bg-slate-50/70 p-5">
          <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500">For more information, contact our team</div>
          <div className="mt-3 grid gap-3 sm:grid-cols-2">
            <a href={telHref} className="flex items-center gap-3 rounded-xl border border-slate-200 bg-white p-3 text-sm text-slate-900 hover:bg-slate-50" data-testid="thanks-contact-phone">
              <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-[#329112]/10 text-[#329112]"><Phone className="h-4 w-4" /></div>
              <div>
                <div className="text-[10px] uppercase tracking-widest text-slate-500">Phone</div>
                <div className="font-bold">{phoneDisplay}</div>
              </div>
            </a>
            <a href={`mailto:${supportEmail}`} className="flex items-center gap-3 rounded-xl border border-slate-200 bg-white p-3 text-sm text-slate-900 hover:bg-slate-50" data-testid="thanks-contact-email">
              <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-[#101876]/10 text-[#101876]"><Mail className="h-4 w-4" /></div>
              <div>
                <div className="text-[10px] uppercase tracking-widest text-slate-500">Email</div>
                <div className="font-bold">{supportEmail}</div>
              </div>
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}
